import { useState, useCallback, useRef } from 'react';

import { streamResponse } from '@components/AskMeAnything/customFetch/streamer';

import { Chunk } from '@/components/AskMeAnything/types';
import { handleStreamingChunk } from '@/components/AskMeAnything/utils/chatUtil';
import { useChatActions } from '@/store/chat/chatStore';

export const useChatStream = () => {
  const [messageStreamRaw, setMessageStreamRaw] = useState<string[]>([]);
  const [completeMessage, setCompleteMessage] = useState<string>('');
  const [isDone, setIsDone] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const { setIsStreaming, setStreamAbortController } = useChatActions();

  const [streamingThoughts, setStreamingThoughts] = useState<string[]>([]);
  const [streamingResponse, setStreamingResponse] = useState('');

  const streamBufferRef = useRef('');
  const isInThoughtsRef = useRef(false);
  const isInResponseRef = useRef(false);
  const responseAccumulatorRef = useRef('');
  const thoughtsAccumulatorRef = useRef('');

  const startStream = useCallback(async (prompt: string, threadId: string) => {
    clearAll();
    setIsStreaming(true);
    const controller = new AbortController();
    controllerRef.current = controller;
    setStreamAbortController(controller);

    try {
      await streamResponse(
        prompt,
        threadId,
        (chunk: Chunk) => {
          if (chunk.type === 'token') {
            handleStreamingChunk(chunk, {
              streamBufferRef,
              isInThoughtsRef,
              isInResponseRef,
              responseAccumulatorRef,
              thoughtsAccumulatorRef,
              setStreamingThoughts,
              setStreamingResponse
            });
            // Append each streamed chunk to the messages
            setMessageStreamRaw(prev => [...prev, chunk.content]);
          } else if (chunk.type === 'message') {
            setCompleteMessage(chunk.content);
          }
        },
        controller.signal
      );
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Stream error:', err);
      }
    } finally {
      setIsStreaming(false);
      setIsDone(true);
    }
  }, []);

  const cancelStream = useCallback(() => {
    controllerRef.current?.abort();
    clearAll();
  }, []);

  const clearAll = useCallback(() => {
    setStreamingThoughts([]);
    setStreamingResponse('');
    setMessageStreamRaw([]);
    setCompleteMessage('');
    setIsStreaming(false);
    setIsDone(false);
    streamBufferRef.current = '';
    responseAccumulatorRef.current = '';
    thoughtsAccumulatorRef.current = '';
    isInThoughtsRef.current = false;
    isInResponseRef.current = false;
  }, []);

  return {
    streamingThoughts,
    streamingResponse,
    messageStreamRaw,
    completeMessage,
    startStream,
    cancelStream,
    isDone
  };
};
